/* eslint-disable react/function-component-definition */
import { AVAILABLE_CURRENCY_CODES } from "components/financials/modern/SelectFinancialsCurrency";
import type { UrlSafeIssuerRef } from "components/issuers/utils";
import {
  decodeIssuerRefFromUrl,
  decodeIssuerRefsFromUrl,
  encodeIssuerRefToUrl,
  encodeIssuerRefsToUrl,
} from "components/issuers/utils";
import lightFormat from "date-fns/lightFormat";
import parseISO from "date-fns/parseISO";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import { createParser } from "nuqs";
import { IFinancialsStatementType, IIndicatorDataSource, IPlatformVertical } from "types/graphqlTypes";

type ObjectIdNode = { objectId: string };
export const objectIdsRefSchema = createParser<ObjectIdNode[]>({
  parse(query) {
    return query
      .split(",")
      .map((objectId) => objectId.trim())
      .filter(notUndefinedOrNull)
      .filter((objectId) => objectId.includes("_"))
      .map((objectId) => ({ objectId }));
  },
  serialize(nodes) {
    return nodes
      .map(({ objectId }) => {
        return objectId;
      })
      .join(",");
  },
});

export const companyIssuersRefSchema = createParser<UrlSafeIssuerRef[]>({
  parse(query) {
    return (decodeIssuerRefsFromUrl(query) || []).map((issuerRef) => ({
      ...issuerRef,
      tickerSymbol: issuerRef.tickerSymbol || "",
      vertical: issuerRef.vertical || null,
      country: issuerRef.country || null,
      exchangeId: issuerRef.exchangeId || null,
    }));
  },
  serialize(issuerRefs) {
    return encodeIssuerRefsToUrl(issuerRefs) || "";
  },
});

export const companyIssuerRefSchema = createParser<UrlSafeIssuerRef>({
  parse(query) {
    const issuerRef = decodeIssuerRefFromUrl(query);
    if (!issuerRef) {
      return null;
    }
    return {
      ...issuerRef,
      tickerSymbol: issuerRef.tickerSymbol || "",
      vertical: issuerRef.vertical || null,
      country: issuerRef.country || null,
      exchangeId: issuerRef.exchangeId || null,
    };
  },
  serialize(issuerRefs) {
    return encodeIssuerRefToUrl(issuerRefs) || "";
  },
});

export const statementTypeSchema = createParser<IFinancialsStatementType | null>({
  parse(query) {
    const statementType = query.toUpperCase();
    const possibleTypes = [
      IFinancialsStatementType.Annual,
      IFinancialsStatementType.Quarterly,
      IFinancialsStatementType.Monthly,
    ];

    return possibleTypes.includes(statementType as IFinancialsStatementType)
      ? (statementType as IFinancialsStatementType)
      : null;
  },
  serialize(statementType) {
    return (statementType || "").toLowerCase();
  },
});

export const interimSchema = createParser<string | null>({
  parse(query) {
    const interim = query.toUpperCase();
    const possibleValues = ["Q1", "Q2", "Q3", "Q4"];
    return possibleValues.includes(interim) ? interim : null;
  },
  serialize(interim) {
    return (interim || "").toUpperCase();
  },
});

export const currencyCodeSchema = createParser<string | null>({
  parse(query) {
    const currency = query.toUpperCase();
    const possibleValues = AVAILABLE_CURRENCY_CODES;
    return possibleValues.includes(currency) ? currency : null;
  },
  serialize(currency) {
    return (currency || "").toLowerCase();
  },
});

export const parseAsDate = createParser({
  parse: (value) => {
    try {
      const date = parseISO(value);
      if (Number.isNaN(date.valueOf())) {
        return null;
      }
      return date;
    } catch {
      return null;
    }
  },
  serialize: (value: Date) => lightFormat(value, "yyyy-MM-dd"),
});

export const platformVerticalSchema = createParser<IPlatformVertical | null>({
  parse(query) {
    const vertical = query.toUpperCase();
    const possibleTypes = [
      IPlatformVertical.Acoes,
      IPlatformVertical.Fiis,
      IPlatformVertical.Stocks,
      IPlatformVertical.Reits,
    ];
    return possibleTypes.includes(vertical as IPlatformVertical) ? (vertical as IPlatformVertical) : null;
  },
  serialize(vertical) {
    return (vertical || "").toLowerCase();
  },
});
export const indicatorDataSourceSchema = createParser<IIndicatorDataSource | null>({
  parse(query) {
    const dataSource = query.toUpperCase();
    const possibleTypes = [IIndicatorDataSource.Cvm, IIndicatorDataSource.Morningstar];
    return possibleTypes.includes(dataSource as IIndicatorDataSource) ? (dataSource as IIndicatorDataSource) : null;
  },
  serialize(dataSource) {
    return (dataSource || "").toLowerCase();
  },
});
